import React from "react";
import { checkedRadio } from "@routes/IconeSvg";
import IconInfo from "@image/icones/DocumentsEmployers/iconInfo.svg";
import BlackTooltip from "@components/tooltip/BlackTooltip";
import { checkBoxVariantsColor as variants } from "@helpers/_constants";
import OverflowTooltipText from "@components/tooltip/OverflowTooltipText";

/**
 * @typedef {("green" | "orange" | "blue" | "gray" | "babyBlue")=} CheckBoxSimpleVariant
 * @typedef CheckBoxSimpleProps
 * @property {string=} id
 * @property {import("react").ReactNode=} label
 * @property {string=} name
 * @property {boolean} checked
 * @property {((e: any) => void)=} onClick
 * @property {boolean=} noRadio
 * @property {string=} className
 *  @property {string=} informations
 * @property {string=} fontLabel
 * @property {boolean=} recurrenceListBox
 * @property {boolean=} withTooltip
 * @property {string=} customClass
 * @property {boolean=} rounded
 * @property {((e: any) => void)=} onChange
 * @property {CheckBoxSimpleVariant=} variant
 * @property {boolean=} invalid
 * @property {boolean=} disabled
 * @property {boolean=} haveHover
 * @property {boolean=} TooltipLabel
 */

function CheckboxSimple(props) {
  const {
    id,
    label = "",
    name,
    disabled = false,
    checked,
    onClick = () => {},
    noRadio = false,
    className = "",
    fontLabel,
    recurrenceListBox = false,
    withTooltip = false,
    customClass = "arh-ml-2",
    rounded = false,
    onChange = () => {},
    variant = "green",
    invalid = false,
    haveHover = true,
    TooltipLabel = false,
    informations = "",
    padding = "arh-p-2.5",
    disableTruncate = false,
    smartTooltip = false
  } = props;

  const inputClass = [
    "arh-relative arh-h-18 arh-w-18 arh-shrink-0 arh-appearance-none arh-border arh-border-solid",
    disabled ? "arh-bg-gray-100" : "arh-bg-white",
    "before:arh-absolute before:arh-left-1/2 before:arh-top-1/2 before:arh-h-2.5 before:arh-w-2.5",
    "before:-arh-translate-x-1/2 before:-arh-translate-y-1/2 before:arh-bg-transparent before:arh-content-['']",
    invalid ? "arh-border-invalidColorTextInput" : "arh-border-greyBlue",
    "arh-cursor-pointer",
    rounded
      ? "arh-rounded-full before:arh-rounded-full"
      : "arh-rounded before:arh-rounded-sm",
    variants[variant]
  ].join(" ");

  const labelClass = [
    "!arh-mb-0 arh-min-w-0 arh-font-poppins arh-text-black",
    disableTruncate ? "arh-whitespace-nowrap" : "arh-truncate",
    fontLabel || "arh-text-xs",
    "arh-cursor-pointer",
    customClass,
    withTooltip ? "arh-block arh-w-full arh-text-left" : ""
  ].join(" ");

  const wrapperClass = [
    "arh-flex arh-min-w-0 arh-items-center arh-rounded",
    padding,
    disabled ? "arh-pointer-events-none arh-cursor-none" : "arh-cursor-pointer",
    haveHover
      ? "hover:arh-bg-gray-200 active:arh-bg-greyActive"
      : "hover:arh-bg-transparent active:arh-bg-transparent",
    className
  ].join(" ");

  if (recurrenceListBox) {
    return (
      <div
        className={`arh-flex arh-h-[2.375rem] arh-items-center arh-justify-between arh-px-[0.625rem] ${className} ${checked ? "arh-bg-[#FFF7F3]" : ""}`}
        onClick={onClick}
        aria-hidden="true"
      >
        {label && (
          <label
            className={`arh-mb-0 arh-min-w-0 arh-font-poppins arh-text-black ${checked ? "arh-text-[#F55611]" : ""} ${fontLabel || "arh-text-xs"}`}
          >
            {label}
          </label>
        )}
        {checked && <div>{checkedRadio()}</div>}
      </div>
    );
  }

  const renderInput = () =>
    !noRadio && (
      <input
        id={id}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className={inputClass}
      />
    );
  const renderLabel = () => {
    if (informations) {
      return (
        <div className="arh-flex arh-w-full arh-items-center arh-justify-between">
          {label && <div className={labelClass}>{label}</div>}

          <BlackTooltip
            title={informations}
            placement="top"
            disableFocusListener
            disableTouchListener
          >
            <img
              src={IconInfo}
              alt="info"
              className="info_content"
            />
          </BlackTooltip>
        </div>
      );
    }

    if (TooltipLabel && label) {
      return (
        <BlackTooltip
          title={label}
          placement="top"
          disableFocusListener
          disableTouchListener
        >
          <div className={labelClass}>{label}</div>
        </BlackTooltip>
      );
    }

    return label ?
      smartTooltip ?
        <OverflowTooltipText className={labelClass} placement="top-start" arrow={true} text={label} /> :
        <div className={labelClass}>{label}</div> :
      null;
  };

  return (
    <div
      className={wrapperClass}
      onClick={onClick}
      aria-hidden="true"
    >
      {renderInput()}
      {renderLabel()}
    </div>
  );
}

export default CheckboxSimple;
